import React from 'react';
import { motion } from 'framer-motion';
import { IconWrapper } from '../../components/common/IconWrapper';
import {
  HiCog,
  HiBolt,
  HiGlobeAlt,
  HiChartBar,
  HiCpuChip,
  HiLightBulb,
  HiServer,
  HiCheckCircle,
  HiArrowRight,
  HiShieldCheck,
  HiSparkles,
  HiClock,
  HiUsers,
  HiWrenchScrewdriver,
  HiArrowTrendingUp
} from 'react-icons/hi2';

const metrics = [
  { value: '-45%', label: 'Unplanned Downtime' },
  { value: '+28%', label: 'OEE Improvement' },
  { value: '500+', label: 'Machines Connected' },
  { value: '$2.5M', label: 'Annual Savings' }
];

const capabilityBlocks = [
  {
    title: 'Predictive Maintenance',
    description: 'Sensor fusion, ML scoring, and prescriptive actions from edge to cloud.',
    bullets: ['Condition monitoring data lake', 'Remaining useful life models', 'Work order automation'],
    icon: HiBolt
  },
  {
    title: 'Connected Operations',
    description: 'Telemetry pipelines unifying PLC, SCADA, MES and ERP signals in real time.',
    bullets: ['Industrial IoT ingestion hub', 'Digital thread APIs', 'Command center dashboards'],
    icon: HiServer
  },
  {
    title: 'Supply & Quality Intelligence',
    description: 'Combine supply chain, production and customer data for resilient operations.',
    bullets: ['Supplier risk cockpit', 'Quality anomaly detection', 'Traceability & genealogy'],
    icon: HiGlobeAlt
  }
];

const solutions = [
  {
    title: 'Industrial IoT Data Platforms',
    description: 'Azure IoT / AWS IoT foundations with device management, streaming ingestion and digital twins.',
    icon: HiServer
  },
  {
    title: 'Digital Twin Simulations',
    description: 'Model lines, assets and products for rapid what-if analysis and closed-loop optimization.',
    icon: HiCpuChip
  },
  {
    title: 'Factory Telemetry Dashboards',
    description: 'Role-based views for plant managers, operators and executives with actionable insights.',
    icon: HiChartBar
  },
  {
    title: 'Automated Quality Inspection',
    description: 'Computer vision pipelines detecting defects, labeling root causes and triggering corrective actions.',
    icon: HiLightBulb
  }
];

const benefits = [
  {
    title: 'Reduced Downtime',
    description: 'Predictive maintenance prevents unexpected equipment failures and extends asset life.',
    icon: HiClock,
    value: '45%'
  },
  {
    title: 'Improved Efficiency',
    description: 'Real-time visibility into operations enables data-driven decision making.',
    icon: HiArrowTrendingUp,
    value: '28%'
  },
  {
    title: 'Cost Savings',
    description: 'Optimize resource utilization and reduce waste across the production line.',
    icon: HiBolt,
    value: '$2.5M'
  },
  {
    title: 'Quality Enhancement',
    description: 'Automated inspection and quality control ensure consistent product standards.',
    icon: HiShieldCheck,
    value: '99.8%'
  }
];

const useCases = [
  {
    industry: 'Automotive Manufacturing',
    challenges: ['Supply chain complexity', 'Quality control', 'Production line optimization'],
    solutions: ['Digital twin simulations', 'Real-time quality monitoring', 'Predictive maintenance'],
    icon: HiCog
  },
  {
    industry: 'Pharmaceutical Production',
    challenges: ['Regulatory compliance', 'Batch traceability', 'Equipment validation'],
    solutions: ['Compliance automation', 'End-to-end traceability', 'Validation workflows'],
    icon: HiShieldCheck
  },
  {
    industry: 'Food & Beverage',
    challenges: ['Food safety', 'Waste reduction', 'Supply chain visibility'],
    solutions: ['HACCP compliance systems', 'Waste analytics', 'Cold chain monitoring'],
    icon: HiGlobeAlt
  },
  {
    industry: 'Aerospace & Defense',
    challenges: ['Precision manufacturing', 'Component traceability', 'Quality assurance'],
    solutions: ['Precision monitoring', 'Digital thread', 'Automated QA systems'],
    icon: HiCpuChip
  }
];

const processSteps = [
  {
    step: '01',
    title: 'Assessment & Strategy',
    description: 'Evaluate current operations, identify pain points, and define digital transformation roadmap.',
    icon: HiChartBar
  },
  {
    step: '02',
    title: 'Pilot Implementation',
    description: 'Deploy proof-of-concept on critical production lines to validate approach and demonstrate value.',
    icon: HiLightBulb
  },
  {
    step: '03',
    title: 'Scale & Integration',
    description: 'Expand solution across facilities, integrate with existing systems, and enable enterprise-wide visibility.',
    icon: HiServer
  },
  {
    step: '04',
    title: 'Optimization & AI',
    description: 'Leverage machine learning and AI to continuously improve operations and predict future needs.',
    icon: HiCpuChip
  }
];

const caseStudy = {
  title: 'Global Automotive Manufacturer',
  description: 'Transformed a 15-facility manufacturing network with IoT sensors, digital twins, and predictive analytics. Reduced unplanned downtime by 45%, improved OEE by 28%, and connected 500+ machines to a unified platform.',
  highlights: [
    { metric: 'Downtime Reduction', value: '45%' },
    { metric: 'OEE Improvement', value: '+28%' },
    { metric: 'Machines Connected', value: '500+' },
    { metric: 'Annual Savings', value: '$2.5M' }
  ]
};

const stack = [
  'Azure IoT Hub / AWS IoT Core',
  'Azure Digital Twins',
  'Kubernetes / Azure Arc',
  'Time Series Insights / Influx',
  'Databricks / Snowflake',
  'Microsoft Fabric',
  'Delta Lake & Kafka',
  'Power BI / Grafana',
  'Edge ML (ONNX, TensorRT)',
  'Computer Vision Toolkits',
  'OPC UA / MQTT / Modbus',
  'Azure DevOps / GitHub Actions'
];

const sectionTitleClass = 'text-3xl md:text-4xl font-bold text-slate-900 dark:text-white';

const heroImage = 'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&w=1600&q=80';

const ManufacturingSolutionPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-white">
      {/* Hero Banner Section */}
      <section className="relative isolate overflow-hidden pt-36 pb-32">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt="Manufacturing & Automotive"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/65 via-slate-950/55 to-slate-950/65" />
          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white drop-shadow-[0_4px_20px_rgba(15,23,42,0.4)]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="inline-flex items-center px-5 py-2 rounded-full bg-blue-50/20 dark:bg-blue-900/30 border border-blue-100/30 dark:border-blue-900/60 mb-6 backdrop-blur-sm"
          >
            <IconWrapper icon={HiCog} className="w-5 h-5 text-blue-300 mr-2" />
            <span className="text-sm font-semibold text-white">Manufacturing & Automotive</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6"
          >
            Smart Factory Platforms
            <span className="block text-blue-400">
              And Connected Product Intelligence
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl text-white/85 leading-relaxed max-w-3xl mx-auto"
          >
            Connect machines, people and supply chains with a blue/black industrial experience built on telemetry, digital twins and AI-driven decisioning.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-wrap gap-4 justify-center mt-8"
          >
            <button className="px-8 py-4 bg-blue-600 text-white rounded-2xl font-semibold shadow-lg hover:bg-blue-700 transition-colors">
              Plan My Smart Factory
            </button>
            <button className="px-8 py-4 border border-white/30 rounded-2xl font-semibold text-white hover:bg-white/10 transition-colors backdrop-blur-sm">
              View Twin Demo
            </button>
          </motion.div>
        </div>
      </section>

      <section className="py-12 border-b border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {metrics.map((metric) => (
            <div key={metric.label} className="rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-6 shadow-sm text-center">
              <p className="text-3xl font-extrabold text-blue-600">{metric.value}</p>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-500 mt-2">{metric.label}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center">
            <h2 className={`${sectionTitleClass} mb-4`}>Modern Engineering Pods</h2>
            <p className="text-lg text-slate-900 dark:text-slate-300">
              Cross-functional teams delivering models, OT/IT integration, UI and change management together.
            </p>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            {capabilityBlocks.map((block) => (
              <motion.div
                key={block.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm space-y-4"
              >
                <div className="inline-flex p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
                  <IconWrapper icon={block.icon} className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">{block.title}</h3>
                <p className="text-sm text-slate-900 dark:text-slate-300">{block.description}</p>
                <ul className="space-y-2">
                  {block.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2 text-sm text-slate-900 dark:text-slate-300">
                      <IconWrapper icon={HiCheckCircle} className="w-4 h-4 text-blue-500 mt-1" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <div className="text-center">
            <h2 className={`${sectionTitleClass} mb-4`}>Solution Accelerators</h2>
            <p className="text-lg text-slate-900 dark:text-slate-300">
              Launch industrial-grade MVPs in weeks with pre-built connectors, UX and DevOps.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((solution) => (
              <motion.div
                key={solution.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm flex gap-4"
              >
                <div className="inline-flex p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
                  <IconWrapper icon={solution.icon} className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">{solution.title}</h3>
                  <p className="text-sm text-slate-900 dark:text-slate-300">{solution.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`${sectionTitleClass} mb-4`}>Key Benefits</h2>
            <p className="text-lg text-slate-900 dark:text-slate-300">
              Measurable outcomes that drive operational excellence and competitive advantage.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-2xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-6 shadow-sm"
              >
                <div className="inline-flex p-3 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 mb-4">
                  <IconWrapper icon={benefit.icon} className="w-6 h-6" />
                </div>
                <p className="text-3xl font-extrabold text-blue-600 mb-2">{benefit.value}</p>
                <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">{benefit.title}</h3>
                <p className="text-sm text-slate-900 dark:text-slate-300">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`${sectionTitleClass} mb-4`}>Industry Use Cases</h2>
            <p className="text-lg text-slate-900 dark:text-slate-300">
              Tailored solutions for diverse manufacturing sectors and their unique challenges.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {useCases.map((useCase) => (
              <motion.div
                key={useCase.industry}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="inline-flex p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
                    <IconWrapper icon={useCase.icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">{useCase.industry}</h3>
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-300 mb-1">Challenges:</h4>
                        <ul className="space-y-1">
                          {useCase.challenges.map((challenge, i) => (
                            <li key={i} className="text-sm text-slate-900 dark:text-slate-400 flex items-start gap-2">
                              <span className="text-blue-500 mt-1">•</span>
                              <span>{challenge}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-300 mb-1">Our Solutions:</h4>
                        <ul className="space-y-1">
                          {useCase.solutions.map((solution, i) => (
                            <li key={i} className="text-sm text-slate-900 dark:text-slate-400 flex items-start gap-2">
                              <IconWrapper icon={HiCheckCircle} className="w-4 h-4 text-blue-500 mt-0.5" />
                              <span>{solution}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className={`${sectionTitleClass} mb-4`}>Our Implementation Process</h2>
            <p className="text-lg text-slate-900 dark:text-slate-300">
              A proven methodology that ensures successful digital transformation from concept to production.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processSteps.map((step) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6 shadow-sm h-full">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-2xl font-extrabold text-blue-600">{step.step}</span>
                    <div className="inline-flex p-2 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300">
                      <IconWrapper icon={step.icon} className="w-5 h-5" />
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-slate-900 dark:text-white">{step.title}</h3>
                  <p className="text-sm text-slate-900 dark:text-slate-300">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[60%_40%] gap-10 items-center">
            <motion.div
              className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-gradient-to-br from-blue-600 via-slate-700 to-slate-900 text-white p-10 shadow-2xl"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="text-3xl font-bold mb-4">{caseStudy.title}</h3>
              <p className="text-lg text-white/90 mb-8">{caseStudy.description}</p>
              <div className="grid grid-cols-2 gap-6">
                {caseStudy.highlights.map((highlight) => (
                  <div key={highlight.metric}>
                    <p className="text-3xl font-extrabold">{highlight.value}</p>
                    <p className="text-sm uppercase tracking-wider text-white/80">{highlight.metric}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              className="p-8 rounded-3xl bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 shadow-xl h-full flex flex-col justify-between"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div>
                <h4 className="text-2xl font-semibold text-slate-900 dark:text-white mb-4">Why manufacturers partner with us</h4>
                <ul className="space-y-3 text-sm text-slate-600 dark:text-slate-300">
                  <li className="flex gap-2">
                    <IconWrapper icon={HiSparkles} className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>Deep expertise in OT/IT integration and industrial protocols.</span>
                  </li>
                  <li className="flex gap-2">
                    <IconWrapper icon={HiBolt} className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>Proven accelerators for IoT, digital twins, and predictive maintenance.</span>
                  </li>
                  <li className="flex gap-2">
                    <IconWrapper icon={HiShieldCheck} className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>Security-first approach with compliance and safety built-in.</span>
                  </li>
                  <li className="flex gap-2">
                    <IconWrapper icon={HiUsers} className="w-4 h-4 text-blue-500 mt-0.5" />
                    <span>Cross-functional teams combining data science, engineering, and operations.</span>
                  </li>
                </ul>
              </div>
              <motion.button
                className="mt-8 group relative px-8 py-4 bg-gradient-to-r from-blue-600 to-slate-800 text-white font-semibold rounded-2xl shadow-lg overflow-hidden"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="relative z-10 flex items-center">
                  Schedule a manufacturing workshop
                  <IconWrapper icon={HiArrowRight} className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-slate-900 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-16 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className={`${sectionTitleClass} text-center mb-10`}>Preferred Technology Stack</h2>
          <p className="text-center text-slate-900 dark:text-slate-300 mb-8 max-w-3xl mx-auto">
            We leverage industry-leading platforms and tools to build robust, scalable manufacturing solutions.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {stack.map((item) => (
              <motion.span
                key={item}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="px-4 py-2 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:shadow-md transition-shadow"
              >
                {item}
              </motion.span>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className={`${sectionTitleClass} mb-4`}>Ready For What’s Next On The Line?</h2>
          <p className="text-lg text-slate-900 dark:text-slate-300 mb-8">
            Co-create smart factory roadmaps, digital twin pilots and connected product experiences with teams that
            know manufacturing constraints and compliance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-colors">
              Book a Smart Factory Workshop
            </button>
            <button className="px-8 py-4 border border-slate-300 dark:border-slate-700 rounded-full font-semibold text-slate-900 dark:text-white hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors">
              Request Case Study
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ManufacturingSolutionPage;

